import { useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, apiErr } from "../../lib/utils";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input, Label, Textarea } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { CloudRain, Plus, XCircle, Pencil } from "lucide-react";

const SEV_COLOR = ["", "text-muted-foreground", "text-blue-400", "text-amber-400", "text-orange-400", "text-red-500"];

function HazardDialog({ hazard, types, onClose, onDone }) {
  const [form, setForm] = useState(hazard || {
    name: "", hazard_type: "heat", severity: 3, countries: "Singapore",
    window_start: "", window_end: "", source: "Manual entry", confidence: "medium", description: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    const payload = {
      ...form, severity: Number(form.severity),
      countries: Array.isArray(form.countries)
        ? form.countries
        : String(form.countries).split(",").map((c) => c.trim()).filter(Boolean),
    };
    try {
      if (hazard?.id) await api.put(`/climate-risk/hazards/${hazard.id}`, payload);
      else await api.post("/climate-risk/hazards", payload);
      toast.success(hazard?.id ? "Hazard updated" : "Hazard created");
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="hazard-dialog">
        <DialogHeader><DialogTitle className="font-display">{hazard?.id ? "Edit hazard" : "New hazard event"}</DialogTitle></DialogHeader>
        <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="hazard-name-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Type</Label>
            <Select value={form.hazard_type} onValueChange={(v) => set("hazard_type", v)}>
              <SelectTrigger data-testid="hazard-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.value || t} value={t.value || t}>{t.label || t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Severity (1–5)</Label>
            <Select value={String(form.severity)} onValueChange={(v) => set("severity", v)}>
              <SelectTrigger data-testid="hazard-severity-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4, 5].map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <div><Label>Countries (comma-separated)</Label>
          <Input value={Array.isArray(form.countries) ? form.countries.join(", ") : form.countries}
            onChange={(e) => set("countries", e.target.value)} data-testid="hazard-countries-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Window start</Label><Input type="date" value={form.window_start || ""} onChange={(e) => set("window_start", e.target.value)} /></div>
          <div><Label>Window end</Label><Input type="date" value={form.window_end || ""} onChange={(e) => set("window_end", e.target.value)} /></div>
        </div>
        <div><Label>Description</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
        <Button variant="primary" className="w-full" onClick={save} disabled={busy || !form.name} data-testid="hazard-save-btn">
          {busy ? "Saving…" : hazard?.id ? "Save changes" : "Create hazard"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function HazardsPanel({ hazards, types, onChanged }) {
  const [dialog, setDialog] = useState(null);
  const close = async (h) => {
    try { await api.post(`/climate-risk/hazards/${h.id}/close`); toast.success("Hazard closed"); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground">Curated hazard library + manual events. Live external feeds plug in here later.</div>
        <Button size="sm" variant="primary" onClick={() => setDialog({})} data-testid="new-hazard-btn"><Plus className="h-3.5 w-3.5" /> New hazard</Button>
      </div>
      <div className="space-y-2">
        {hazards.map((h) => (
          <div key={h.id} className="card-flat p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`hazard-row-${h.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <CloudRain className={`h-4 w-4 ${SEV_COLOR[h.severity] || ""}`} />
                <span className="font-semibold text-sm">{h.name}</span>
                <Badge variant="outline" className="text-[9px] uppercase">{h.hazard_type_label || h.hazard_type}</Badge>
                {h.legacy && <Badge variant="warning" className="text-[9px]" title="This hazard type has been retired from the shared taxonomy. The entry is preserved for history and can no longer be edited.">legacy — read-only history</Badge>}
                <span className={`text-[11px] font-mono ${SEV_COLOR[h.severity]}`}>severity {h.severity}/5</span>
                {h.status !== "active" && <Badge variant="outline" className="text-[9px] uppercase opacity-60">{h.status}</Badge>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">{h.description}</div>
              <div className="text-[10px] text-muted-foreground/70 font-mono mt-1">
                {(h.countries || []).join(" · ")} · {h.window_start || "?"} → {h.window_end || "?"} · {h.source} · confidence {h.confidence}
              </div>
            </div>
            {h.status === "active" && !h.legacy && (
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" title="Edit" onClick={() => setDialog(h)} data-testid={`hazard-edit-${h.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" title="Close hazard" onClick={() => close(h)} data-testid={`hazard-close-${h.id}`}><XCircle className="h-3.5 w-3.5 text-red-400" /></Button>
              </div>
            )}
          </div>
        ))}
        {!hazards.length && <div className="text-sm text-muted-foreground py-8 text-center">No hazards yet — add one to start scoring.</div>}
      </div>
      {dialog && <HazardDialog hazard={dialog.id ? dialog : null} types={types} onClose={() => setDialog(null)} onDone={onChanged} />}
    </div>
  );
}

export function formatBandColor(band) {
  return { Low: "text-muted-foreground", Moderate: "text-blue-400", Elevated: "text-amber-400",
           High: "text-orange-400", Critical: "text-red-500" }[band] || "text-muted-foreground";
}

export function bandBorder(band) {
  return { High: "border-l-orange-400", Critical: "border-l-red-500", Elevated: "border-l-amber-400" }[band] || "border-l-border";
}

export { formatSgd };
